import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

import { apiUrl } from '../../../core/config/api-endpoints';
import {
  PartnerCreatePayload,
  PartnerDashboardCounts,
  PartnerDeactivationDependenciesResponse,
  PartnerFiltersResponse,
  PartnerListQuery,
  PartnerListResponse,
  PartnerMutationResponse,
  PartnerRead,
  PartnerServiceCatalogResponse,
  PartnerStatus,
  PartnerStatusPayload,
  PartnerUpdatePayload,
  PartnerVendorOption,
  PartnerVendorOptionsResponse,
} from '../models/partner-management';
import { PartnerFeedbackRatingResponse } from '../models/partner-feedback-rating-row';
import { PartnerJobHistoryResponse } from '../models/partner-job-history-row';
import {
  PartnerDocumentDetailResponse,
  PartnerDocumentListQuery,
  PartnerDocumentListResponse,
  PartnerDocumentReviewPayload,
  PartnerDocumentStats,
} from '../models/partner-document-listing-row';
import {
  PartnerAvailabilityDayPayload,
  PartnerAvailabilityDayResponse,
  PartnerScheduleQuery,
  PartnerScheduleResponse,
} from '../models/partner-schedule';
import {
  PartnerTrackingRouteResponse,
  PartnerTrackingTimelineResponse,
} from '../models/partner-real-time-tracking';

@Injectable({ providedIn: 'root' })
export class PartnerManagementService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = apiUrl('/portal/partners');
  private partnerServiceCatalog$?: Observable<PartnerServiceCatalogResponse>;

  listPartners(query: PartnerListQuery): Observable<PartnerListResponse> {
    return this.http.post<PartnerListResponse>(
      `${this.baseUrl}/search`,
      this.buildListPayload(query),
    );
  }

  getDashboardCounts(): Observable<PartnerDashboardCounts> {
    return this.http.get<PartnerDashboardCounts>(`${this.baseUrl}/stats`);
  }

  getPartnerServiceCatalog(refresh = false): Observable<PartnerServiceCatalogResponse> {
    if (refresh) {
      this.partnerServiceCatalog$ = undefined;
    }
    this.partnerServiceCatalog$ ??= this.http
      .get<PartnerServiceCatalogResponse>(`${this.baseUrl}/available-services`)
      .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    return this.partnerServiceCatalog$;
  }

  getPartnerVendorOptions(): Observable<PartnerVendorOptionsResponse> {
    return this.http.get<PartnerVendorOptionsResponse>(`${this.baseUrl}/vendor-options`);
  }

  getPartnerFilters(): Observable<PartnerFiltersResponse> {
    return this.http.get<PartnerFiltersResponse>(`${this.baseUrl}/filters`);
  }

  getVendorPartnerFilters(vendorId: string): Observable<PartnerFiltersResponse> {
    return this.http.get<PartnerFiltersResponse>(
      `${this.baseUrl}/vendor/${encodeURIComponent(vendorId)}/filters`,
    );
  }

  createPartner(payload: PartnerCreatePayload): Observable<PartnerMutationResponse> {
    return this.http.post<PartnerMutationResponse>(this.baseUrl, payload);
  }

  previewPartners(file: File): Observable<{ rows: Record<string, string>[] }> {
    const body = new FormData();
    body.append('file', file);
    return this.http.post<{ rows: Record<string, string>[] }>(
      `${this.baseUrl}/import/preview`,
      body,
    );
  }

  importPartnerRow(row: Record<string, string>): Observable<{ partner_id: string }> {
    return this.http.post<{ partner_id: string }>(`${this.baseUrl}/import/row`, row);
  }

  getPartner(partnerId: string): Observable<PartnerRead> {
    return this.http.get<PartnerRead>(`${this.baseUrl}/${encodeURIComponent(partnerId)}`);
  }

  getPartnerProfilePhoto(partnerId: string): Observable<{ profile_photo_url: string | null }> {
    return this.http.get<{ profile_photo_url: string | null }>(
      `${this.baseUrl}/${encodeURIComponent(partnerId)}/profile-photo`,
    );
  }

  getPartnerDeactivationDependencies(
    partnerId: string,
  ): Observable<PartnerDeactivationDependenciesResponse> {
    return this.http.get<PartnerDeactivationDependenciesResponse>(
      `${this.baseUrl}/${encodeURIComponent(partnerId)}/deactivation-dependencies`,
    );
  }

  getPartnerSchedule(
    partnerId: string,
    query: PartnerScheduleQuery,
  ): Observable<PartnerScheduleResponse> {
    return this.http.post<PartnerScheduleResponse>(
      `${this.baseUrl}/${encodeURIComponent(partnerId)}/schedule`,
      query,
    );
  }

  getPartnerJobHistory(
    partnerId: string,
    payload: Record<string, unknown>,
  ): Observable<PartnerJobHistoryResponse> {
    return this.http.post<PartnerJobHistoryResponse>(
      `${this.baseUrl}/${encodeURIComponent(partnerId)}/job-history`,
      payload,
    );
  }

  getPartnerFeedbackRatings(
    partnerId: string,
    payload: Record<string, unknown>,
  ): Observable<PartnerFeedbackRatingResponse> {
    return this.http.post<PartnerFeedbackRatingResponse>(
      `${this.baseUrl}/${encodeURIComponent(partnerId)}/feedback-ratings`,
      payload,
    );
  }

  getPartnerDocuments(partnerId: string): Observable<PartnerDocumentDetailResponse> {
    return this.http.get<PartnerDocumentDetailResponse>(
      `${this.baseUrl}/${encodeURIComponent(partnerId)}/documents`,
    );
  }

  getPartnerTrackingTimeline(
    partnerId: string,
    trackingDate?: string,
  ): Observable<PartnerTrackingTimelineResponse> {
    return this.http.get<PartnerTrackingTimelineResponse>(
      this.partnerTrackingUrl(partnerId, 'timeline', trackingDate),
    );
  }

  getPartnerTrackingRoute(
    partnerId: string,
    trackingDate?: string,
  ): Observable<PartnerTrackingRouteResponse> {
    return this.http.get<PartnerTrackingRouteResponse>(
      this.partnerTrackingUrl(partnerId, 'route', trackingDate),
    );
  }

  getPartnerDocumentStats(): Observable<PartnerDocumentStats> {
    return this.http.get<PartnerDocumentStats>(`${this.baseUrl}/documents/stats`);
  }

  listPartnerDocuments(query: PartnerDocumentListQuery): Observable<PartnerDocumentListResponse> {
    return this.http.post<PartnerDocumentListResponse>(
      `${this.baseUrl}/documents`,
      this.buildDocumentListPayload(query),
    );
  }

  reviewPartnerDocument(
    partnerId: string,
    documentId: string,
    payload: PartnerDocumentReviewPayload,
  ): Observable<PartnerMutationResponse> {
    return this.http.patch<PartnerMutationResponse>(
      `${this.baseUrl}/${encodeURIComponent(partnerId)}/documents/${encodeURIComponent(documentId)}`,
      payload,
    );
  }

  updatePartnerDayAvailability(
    partnerId: string,
    payload: PartnerAvailabilityDayPayload,
  ): Observable<PartnerAvailabilityDayResponse> {
    return this.http.post<PartnerAvailabilityDayResponse>(
      `${this.baseUrl}/${encodeURIComponent(partnerId)}/availability/day`,
      payload,
    );
  }

  updatePartner(
    partnerId: string,
    payload: PartnerUpdatePayload,
  ): Observable<PartnerMutationResponse> {
    return this.http.put<PartnerMutationResponse>(
      `${this.baseUrl}/${encodeURIComponent(partnerId)}`,
      payload,
    );
  }

  updatePartnerStatus(
    partnerId: string,
    statusOrPayload: PartnerStatus | PartnerStatusPayload,
  ): Observable<PartnerMutationResponse> {
    const payload =
      typeof statusOrPayload === 'string' ? { status: statusOrPayload } : statusOrPayload;
    return this.http.patch<PartnerMutationResponse>(
      `${this.baseUrl}/${encodeURIComponent(partnerId)}/status`,
      payload,
    );
  }

  private partnerTrackingUrl(
    partnerId: string,
    type: 'timeline' | 'route',
    trackingDate?: string,
  ): string {
    const url = `${this.baseUrl}/${encodeURIComponent(partnerId)}/tracking/${type}`;
    if (!trackingDate?.trim()) {
      return url;
    }
    return `${url}?tracking_date=${encodeURIComponent(trackingDate)}`;
  }

  private buildListPayload(query: PartnerListQuery): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      page: query.page,
      limit: query.pageSize,
      search: query.search,
      cities: query.cities,
      client_ids: query.clientIds,
      branch_ids: query.branchIds,
      vendor_ids: query.vendorIds,
      categories: query.categories,
      service_ids: query.serviceIds,
      statuses: query.statuses,
      runtime_statuses: query.runtimeStatuses,
      document_statuses: query.documentStatuses,
      onboarded_from: query.onboardedFrom,
      onboarded_to: query.onboardedTo,
    };

    for (const key of Object.keys(payload)) {
      const value = payload[key];
      if (
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0)
      ) {
        delete payload[key];
      }
    }

    return payload;
  }

  private buildDocumentListPayload(query: PartnerDocumentListQuery): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      page: query.page,
      limit: query.pageSize,
      search: query.search,
      statuses: query.statuses,
      uploaded_from: query.uploadedFrom,
      uploaded_to: query.uploadedTo,
    };

    for (const key of Object.keys(payload)) {
      const value = payload[key];
      if (value === undefined || value === null || (typeof value === 'string' && !value.trim())) {
        delete payload[key];
      }
    }

    return payload;
  }
}
